import { Box } from "@mui/material";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { ErrorBoundary } from "../../ErrorBoundary";
import { type FlatSwitchOption, SystemFlatSwitch } from "../../UIToolkit/Clickable/SystemFlatSwitch";
import { EmptyState } from "../../UIToolkit/molecules/EmptyState";
import { MultiSizePopupState } from "../../UIToolkit/Popups/MultiSizePopup/utils";
import { SCHEDULE_EXPORT_TEST_ID, ScheduleExportTab } from "./consts";
import { MonthlyExportTab } from "./MonthlyExportTab";
import { useScheduleExportAnalytics } from "./useScheduleExportAnalytics";
import { WeeklyExportTab } from "./WeeklyExportTab";

export type ScheduleExportBodyProps = {
	/**
	 * The popup's size state, in full.
	 *
	 * It arrives as state rather than a mount/unmount so the tab, both date ranges and the monthly
	 * settings survive a round trip. HIDDEN has to be told apart from MINIMIZED rather than folded into
	 * one flag: the popup is given `keepBodyMounted`, so it renders this body in every state — hiding
	 * therefore only looks like hiding if the body collapses itself.
	 */
	sizeState: MultiSizePopupState;
	weekIndex: number;
	onClose: () => void;
};

export const ScheduleExportBody: FC<ScheduleExportBodyProps> = ({ sizeState, weekIndex, onClose }) => {
	const { t } = useTranslation();
	const isMaximized = sizeState === MultiSizePopupState.MAXIMIZED;
	// Kept mounted so nothing is lost, but taken out of layout so the popup collapses to its header.
	const isHidden = sizeState === MultiSizePopupState.HIDDEN;
	const { chosenDepartment, hospitalData } = useHospitalData();
	const [tab, setTab] = useState<ScheduleExportTab>(ScheduleExportTab.Monthly);

	/**
	 * Which tab the BODY renders. Switching tabs mounts either a multi-page PDF render or a Fortune
	 * Sheet canvas, and doing that in the same commit as the click froze the popup mid-press with no
	 * sign the click had registered. The strip below reads the real `tab` so the selection moves
	 * instantly; the expensive subtree swaps in a second, lower-priority render.
	 */
	const renderedTab = useDebouncedValue(tab);

	// Deliberately the rendered tab, not the clicked one: this stamps every event the mounted tab
	// fires, and `trackTabChanged` passes the newly clicked tab explicitly.
	const analytics = useScheduleExportAnalytics({ props: { department_id: chosenDepartment.department_id, mode: renderedTab } });

	// Open and close, carrying the legacy properties the existing Mixpanel reports break down by. The
	// body mounts with the popup and unmounts with it, so its lifecycle IS the popup's.
	const analyticsRef = useRef(analytics);
	analyticsRef.current = analytics;
	// Read through a ref with no deps: this pair describes the popup's lifetime, and depending on the
	// week would fire a spurious close/open every time the user paged the grid behind this non-modal
	// popup. `weekIndex` is only the value to STAMP, not a reason to re-fire.
	const openedWeekRef = useRef({ weekIndex, date: hospitalData.weeks?.[weekIndex]?.weeklyShifts?.[0]?.days?.[0]?.date?.toFormat("yyyy-MM-dd") });
	useEffect(() => {
		analyticsRef.current.trackOpened({ current_week_index: openedWeekRef.current.weekIndex, current_week_date: openedWeekRef.current.date });
		return () => analyticsRef.current.trackClosed();
	}, []);

	const tabs = useMemo<FlatSwitchOption<ScheduleExportTab>[]>(
		() => [
			{ label: t("download.by.person"), value: ScheduleExportTab.Monthly, "data-testid": `${SCHEDULE_EXPORT_TEST_ID}-tab-monthly` },
			{ label: t("download.by.week"), value: ScheduleExportTab.Weekly, "data-testid": `${SCHEDULE_EXPORT_TEST_ID}-tab-weekly` },
		],
		[t],
	);

	const handleTabChange = (next: ScheduleExportTab) => {
		setTab(next);
		analytics.trackTabChanged(next);
	};

	return (
		<Box
			data-testid={`${SCHEDULE_EXPORT_TEST_ID}-body`}
			sx={{
				display: isHidden ? "none" : "flex",
				flexDirection: "column",
				gap: 2,
				p: 2,
				minWidth: 0,
				...(isMaximized ? { width: { xs: "100vw", sm: "96vw", xxl: "84vw" }, height: { xs: "100dvh", sm: "80vh" } } : { width: "100%" }),
			}}
		>
			{/* Centred like the Daily Printout modal's type switch, which this now visually matches. */}
			<Box sx={{ display: "flex", justifyContent: "center" }}>
				<SystemFlatSwitch
					data-testid={`${SCHEDULE_EXPORT_TEST_ID}-tabs`}
					// The strip itself, not one tab — and a static name, since the test id above is a
					// template literal and cannot be an anchor.
					data-annie-target="schedule-export-tabs"
					options={tabs}
					activeIndexOrValue={tab}
					onSwitch={(option) => handleTabChange(option.value)}
					textWrap={"nowrap"}
					paddingOverrides={{ xs: "6px 20px" }}
				/>
			</Box>

			<ErrorBoundary fallback={<EmptyState title={t("download.preview.error")} />}>
				{renderedTab === ScheduleExportTab.Weekly ? (
					<WeeklyExportTab showPreview={isMaximized} weekIndex={weekIndex} analytics={analytics} onDownloaded={onClose} />
				) : (
					<MonthlyExportTab showPreview={isMaximized} analytics={analytics} onDownloaded={onClose} />
				)}
			</ErrorBoundary>
		</Box>
	);
};
